import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { tasksAxios } from '../../api/tasksApi'
import type {
  CreateTaskInput,
  CreateTaskItemRequest,
  Priority,
  Status,
  Task,
  TaskItemDto,
  TasksState,
  UpdateTaskItemRequest,
} from '../../Models/Task'

/** API may send string enums; older hosts sent numeric .NET enum values. */
function mapPriority(raw: TaskItemDto['priority']): Priority | undefined {
  if (raw == null) return undefined
  if (typeof raw === 'string') {
    if (raw === 'p1' || raw === 'p2' || raw === 'p3' || raw === 'p4') return raw
    return undefined
  }
  const order: Priority[] = ['p1', 'p2', 'p3', 'p4']
  if (typeof raw === 'number' && raw >= 0 && raw < order.length) return order[raw]
  return undefined
}

function mapStatus(raw: TaskItemDto['status']): Status | undefined {
  if (raw == null) return undefined
  if (typeof raw === 'string') {
    if (
      raw === 'todo' ||
      raw === 'in_progress' ||
      raw === 'review' ||
      raw === 'done'
    ) {
      return raw
    }
    return undefined
  }
  const order: Status[] = ['todo', 'in_progress', 'review', 'done']
  if (typeof raw === 'number' && raw >= 0 && raw < order.length) return order[raw]
  return undefined
}

function mapDto(d: TaskItemDto): Task {
  return {
    id: d.id,
    title: d.title,
    metaLine: d.metaLine ?? undefined,
    scheduledDate: d.scheduledDate ?? undefined,
    deadlineDate: d.deadlineDate ?? undefined,
    createdAt: d.createdAt,
    completedAt: d.completedAt ?? undefined,
    description: d.description ?? undefined,
    priority: mapPriority(d.priority),
    status: mapStatus(d.status),
    project: d.project ?? undefined,
    labels: d.labels ?? undefined,
    owner: d.owner ?? undefined,
    subtasks: d.subtasks ?? undefined,
  }
}

/**
 * A list fetch that finished after a tag PATCH can return a stale row where `labels` is
 * missing from JSON (treated as undefined here). If we had tags in the store, keep them
 * until a response includes an explicit `labels` array (including `[]` when all cleared).
 */
function mergeStaleOmittedLabels(previous: Task | undefined, next: Task): Task {
  if (previous?.labels?.length && next.labels === undefined) {
    return { ...next, labels: previous.labels }
  }
  return next
}

/** Merge PATCH body into a task draft for instant UI while the request is in flight. */
function applyOptimisticTaskPatch(task: Task, changes: UpdateTaskItemRequest) {
  if (changes.title !== undefined) {
    task.title = changes.title ?? ''
  }
  if (changes.description !== undefined) {
    if (changes.description === null) {
      delete task.description
    } else {
      task.description = changes.description
    }
  }
  if (changes.metaLine !== undefined) {
    if (changes.metaLine === null) {
      delete task.metaLine
    } else {
      task.metaLine = changes.metaLine
    }
  }
  if (changes.scheduledDate !== undefined) {
    if (changes.scheduledDate === null) {
      delete task.scheduledDate
    } else {
      task.scheduledDate = changes.scheduledDate
    }
  }
  if (changes.deadlineDate !== undefined) {
    if (changes.deadlineDate === null) {
      delete task.deadlineDate
    } else {
      task.deadlineDate = changes.deadlineDate
    }
  }
  if (changes.completedAt !== undefined) {
    if (changes.completedAt === null) {
      delete task.completedAt
    } else {
      task.completedAt = changes.completedAt
    }
  }
  if (changes.priority !== undefined) {
    if (changes.priority === null) {
      delete task.priority
    } else {
      task.priority = changes.priority
    }
  }
  if (changes.status !== undefined) {
    if (changes.status === null) {
      delete task.status
    } else {
      task.status = changes.status
    }
  }
  if (changes.project !== undefined) {
    if (changes.project === null) {
      delete task.project
    } else {
      task.project = changes.project
    }
  }
  if (changes.labels !== undefined) {
    if (changes.labels === null) {
      delete task.labels
    } else {
      task.labels = changes.labels
    }
  }
  if (changes.owner !== undefined) {
    if (changes.owner === null) {
      delete task.owner
    } else {
      task.owner = changes.owner
    }
  }
  if (changes.subtasks !== undefined) {
    if (changes.subtasks === null) {
      delete task.subtasks
    } else {
      task.subtasks = changes.subtasks.map((s) => ({ ...s }))
    }
  }
}

function errorMessage(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { error?: string } | undefined
    return data?.error ?? e.message ?? fallback
  }
  if (e instanceof Error) return e.message
  if (
    typeof e === 'object' &&
    e !== null &&
    'message' in e &&
    typeof (e as { message: unknown }).message === 'string'
  ) {
    return (e as { message: string }).message
  }
  return fallback
}

export const fetchTasks = createAsyncThunk<
  TaskItemDto[],
  void,
  { rejectValue: unknown }
>('tasks/fetchAll', async (_, thunkAPI) => {
  try {
    const { data } = await tasksAxios.get<TaskItemDto[]>('/GetTaskItems')
    return data
  } catch (error) {
    console.error(error)
    return thunkAPI.rejectWithValue(error)
  }
})

export const fetchTasksByDate = createAsyncThunk<
  TaskItemDto[],
  string,
  { rejectValue: unknown }
>('tasks/fetchByDate', async (date, thunkAPI) => {
  try {
    const { data } = await tasksAxios.get<TaskItemDto[]>(`/GetTaskItemsByDate/${date}`)
    return data
  } catch (error) {
    console.error(error)
    return thunkAPI.rejectWithValue(error)
  }
})

export const createTask = createAsyncThunk<
  TaskItemDto,
  CreateTaskInput,
  { rejectValue: unknown }
>('tasks/create', async (input, thunkAPI) => {
  const body: CreateTaskItemRequest = {
    title: input.title.trim(),
    scheduledDate: input.scheduledDate,
    deadlineDate: input.deadlineDate,
    metaLine: input.metaLine,
  }
  try {
    const { data } = await tasksAxios.post<TaskItemDto>('/CreateTaskItem', body)
    return data
  } catch (error) {
    console.error(error)
    return thunkAPI.rejectWithValue(error)
  }
})

export const updateTask = createAsyncThunk<
  TaskItemDto,
  { id: string; changes: UpdateTaskItemRequest },
  { rejectValue: unknown }
>('tasks/update', async ({ id, changes }, thunkAPI) => {
  try {
    const { data } = await tasksAxios.patch<TaskItemDto>(`/UpdateTaskItem/${id}`, changes)
    return data
  } catch (error) {
    console.error(error)
    return thunkAPI.rejectWithValue(error)
  }
})

export const deleteTask = createAsyncThunk<string, string, { rejectValue: unknown }>(
  'tasks/delete',
  async (id, thunkAPI) => {
    try {
      await tasksAxios.delete(`/DeleteTaskItem/${id}`)
      return id
    } catch (error) {
      console.error(error)
      return thunkAPI.rejectWithValue(error)
    }
  }
)

const initialState: TasksState = {
  items: [],
  status: 'idle',
}

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTasks.pending, (state) => {
        state.status = 'loading'
        state.error = undefined
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const prevById = new Map(state.items.map((t) => [t.id, t] as [string, Task]))
        const nextItems: Task[] = []
        for (const dto of action.payload) {
          const m = mapDto(dto)
          const merged = mergeStaleOmittedLabels(prevById.get(dto.id), m)
          nextItems.push(merged)
        }
        state.items = nextItems
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.status = 'failed'
        state.error = errorMessage(
          action.payload ?? action.error,
          'Failed to load tasks.'
        )
      })

      .addCase(fetchTasksByDate.pending, (state) => {
        state.status = 'loading'
        state.error = undefined
      })
      .addCase(fetchTasksByDate.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const byId = new Map(state.items.map((t) => [t.id, t] as [string, Task]))
        for (const dto of action.payload) {
          const prev = byId.get(dto.id)
          const m = mapDto(dto)
          byId.set(dto.id, mergeStaleOmittedLabels(prev, m))
        }
        state.items = Array.from(byId.values())
      })
      .addCase(fetchTasksByDate.rejected, (state, action) => {
        state.status = 'failed'
        state.error = errorMessage(
          action.payload ?? action.error,
          'Failed to load tasks for date.'
        )
      })

      .addCase(createTask.pending, (state) => {
        state.status = 'loading'
        state.error = undefined
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const mapped = mapDto(action.payload)
        const idx = state.items.findIndex((t) => t.id === mapped.id)
        if (idx >= 0) state.items[idx] = mapped
        else state.items.push(mapped)
      })
      .addCase(createTask.rejected, (state, action) => {
        state.status = 'failed'
        state.error = errorMessage(action.payload ?? action.error, 'Failed to create task.')
      })

      .addCase(updateTask.pending, (state, action) => {
        state.error = undefined
        const { id, changes } = action.meta.arg
        const task = state.items.find((t) => t.id === id)
        if (task) {
          applyOptimisticTaskPatch(task, changes)
        }
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const mapped = mapDto(action.payload)
        const idx = state.items.findIndex((t) => t.id === mapped.id)
        if (idx >= 0) state.items[idx] = mapped
        else state.items.push(mapped)
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.status = 'failed'
        state.error = errorMessage(action.payload ?? action.error, 'Failed to update task.')
      })

      .addCase(deleteTask.pending, (state) => {
        state.status = 'loading'
        state.error = undefined
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.items = state.items.filter((t) => t.id !== action.payload)
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.status = 'failed'
        state.error = errorMessage(action.payload ?? action.error, 'Failed to delete task.')
      })
  },
})

export default tasksSlice.reducer

export type {
  CreateTaskInput,
  CreateTaskItemRequest,
  Priority,
  Status,
  Subtask,
  SubtaskDto,
  Task,
  TaskItemDto,
  TaskOwner,
  TaskOwnerDto,
  TasksState,
  UpdateTaskItemRequest,
} from '../../Models/Task'
